import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "react-query";
import { getServiceList } from "apis";
import { ServiceDTO } from "types/DTOs/service/service.dto";
import { ServiceCategory } from "types";
import TermsModal from "components/common/TermsModal";
import PrivacyModal from "components/common/PrivacyModal";
import { getImageUrl } from "utils/image.util";

const categories = [
  { emoji: "💬", label: "수다·티타임", sub: "퇴근길 통화, 속마음 털어놓기", category: ServiceCategory.CHAT, color: "from-pink-500/10 to-rose-500/10 text-pink-600 border-pink-200" },
  { emoji: "🎮", label: "게임·듀오", sub: "롤, 발로란트 멘탈 케어 듀오", category: ServiceCategory.GAME, color: "from-purple-500/10 to-indigo-500/10 text-purple-600 border-purple-200" },
  { emoji: "🔮", label: "타로·운세", sub: "연애운, 이직운, 심층 리딩", category: ServiceCategory.TAROT, color: "from-violet-500/10 to-purple-500/10 text-violet-600 border-violet-200" },
  { emoji: "💡", label: "고민·피드백", sub: "이력서 첨삭, 스타트업 커피챗", category: ServiceCategory.FEEDBACK, color: "from-amber-500/10 to-orange-500/10 text-amber-600 border-amber-200" },
  { emoji: "💻", label: "랜선 스터디", sub: "딴짓 방지 1:1 캠 집중 스터디", category: ServiceCategory.STUDY, color: "from-blue-500/10 to-cyan-500/10 text-blue-600 border-blue-200" },
  { emoji: "✨", label: "자유주제", sub: "나만의 특별한 재능과 경험 공유", category: ServiceCategory.FREE, color: "from-emerald-500/10 to-teal-500/10 text-emerald-600 border-emerald-200" },
];

const mockCuratedServices = [
  {
    id: "sample-tarot",
    title: "연애 & 재회운 1:1 심층 타로 (30분 솔직 리딩)",
    description: "답답한 마음, 타로 카드로 상대방의 속마음과 앞으로의 흐름을 짚어드립니다.",
    category: ServiceCategory.TAROT,
    price: 15000,
    durationMinutes: 30,
    host: { name: "타로마스터 유나", profileImage: "" },
    slotsText: "오늘 예약 가능",
    rating: "4.9",
    reviewCount: 38,
    bgGradient: "from-violet-500 to-indigo-600",
    emoji: "🔮",
  },
  {
    id: "sample-chat",
    title: "오늘 하루 힘들었죠? 들어주는 30분 티타임",
    description: "친구에게 말하기 어려운 고민, 편견 없이 경청하고 따뜻하게 위로해 드려요.",
    category: ServiceCategory.CHAT,
    price: 10000,
    durationMinutes: 30,
    host: { name: "달콤한라떼", profileImage: "" },
    slotsText: "2개 시간대 가능",
    rating: "5.0",
    reviewCount: 52,
    bgGradient: "from-pink-500 to-rose-500",
    emoji: "☕",
  },
  {
    id: "sample-game",
    title: "롤(LoL) 다이아 서폿이 알려주는 라인전 & 시야 강의",
    description: "티어 상승을 위한 실전 듀오 플레이 및 피드백! 멘탈 절대 안 터집니다.",
    category: ServiceCategory.GAME,
    price: 20000,
    durationMinutes: 60,
    host: { name: "협곡의바람", profileImage: "" },
    slotsText: "주말 가능",
    rating: "4.9",
    reviewCount: 29,
    bgGradient: "from-purple-600 to-blue-600",
    emoji: "🎮",
  },
  {
    id: "sample-career",
    title: "5년차 IT PM의 이직 포트폴리오 1:1 커피챗",
    description: "비전공자에서 IT 기획자로 이직한 경험을 바탕으로 현실적인 조언을 드립니다.",
    category: ServiceCategory.FEEDBACK,
    price: 30000,
    durationMinutes: 40,
    host: { name: "프로덕트리드", profileImage: "" },
    slotsText: "내일 20:00 가능",
    rating: "5.0",
    reviewCount: 19,
    bgGradient: "from-amber-500 to-orange-600",
    emoji: "💼",
  },
  {
    id: "sample-study",
    title: "1시간 뽀모도로 캠 켜고 빡집중 모각코 / 자격증",
    description: "혼자 공부하면 딴짓하는 분! 서로 화면 공유하고 목표 달성하는 집중 세션.",
    category: ServiceCategory.STUDY,
    price: 5000,
    durationMinutes: 60,
    host: { name: "새벽코더", profileImage: "" },
    slotsText: "매일 밤 22시",
    rating: "4.8",
    reviewCount: 44,
    bgGradient: "from-blue-600 to-cyan-600",
    emoji: "💻",
  },
  {
    id: "sample-english",
    title: "원어민 같은 발음 교정 & 20분 프리토킹 영어",
    description: "해외 체류 10년차가 편안한 분위기에서 자연스러운 일상 회화를 이끌어드려요.",
    category: ServiceCategory.FREE,
    price: 18000,
    durationMinutes: 30,
    host: { name: "Chloe", profileImage: "" },
    slotsText: "오늘 19:30 가능",
    rating: "4.9",
    reviewCount: 31,
    bgGradient: "from-emerald-500 to-teal-600",
    emoji: "🗣️",
  },
];

const isSlotAvailable = (s: any): boolean => {
  if (s.isBooked || !s.date) return false;
  const timeStr = s.endTime || s.startTime || "23:59:59";
  const slotDate = new Date(`${s.date}T${timeStr.length === 5 ? timeStr + ":00" : timeStr}`);
  return !isNaN(slotDate.getTime()) && slotDate.getTime() > Date.now();
};

export default function Landing() {
  const navigate = useNavigate();
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);

  const { data: servicesData } = useQuery("landingServices", () =>
    getServiceList()
  );

  const dbServices: ServiceDTO[] = (servicesData?.data?.data ?? [])
    .filter((s: ServiceDTO) => (s.schedules?.filter(isSlotAvailable).length ?? 0) > 0);

  return (
    <div className="animate-fadeIn bg-white text-achromatic-900 selection:bg-primary-100 selection:text-primary-700">
      
      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
          1. HERO SECTION (역동적인 헤드라인 + 플로팅 비주얼 쇼케이스)
         ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="relative overflow-hidden pt-12 pb-24 mobile:pt-8 mobile:pb-16 bg-gradient-to-b from-primary-50/50 via-white to-white border-b border-achromatic-100">
        {/* Decorative blur lights */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[350px] bg-gradient-to-tr from-primary-200/40 to-violet-300/30 blur-3xl -z-10 pointer-events-none rounded-full" />

        <div className="max-w-[1140px] mx-auto px-6 mobile:px-5">
          {/* Top Badge */}
          <div className="flex justify-center mb-6">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white border border-primary-200/80 shadow-sm shadow-primary-500/5 text-[13px] font-semibold text-primary-600">
              <span className="flex h-2 w-2 rounded-full bg-primary-500 animate-pulse" />
              <span>누구나 내 시간을 수익으로 만드는 1:1 세션 마켓플레이스</span>
            </div>
          </div>

          {/* Main Title & Subtitle */}
          <div className="text-center max-w-[760px] mx-auto mb-10">
            <h1 className="text-[48px] mobile:text-[32px] font-extrabold text-achromatic-900 leading-[1.2] tracking-tight mb-6">
              당신의 30분,<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-600 via-indigo-600 to-violet-600">
                누군가에게는 꼭 필요한 가치
              </span>가 됩니다.
            </h1>
            <p className="text-[17px] mobile:text-[15px] text-achromatic-500 leading-relaxed font-normal">
              속마음 털어놓는 수다부터 롤 듀오 코칭, 심층 타로, 이력서 피드백까지.<br className="mobile:hidden" />
              남는 시간을 등록해 부수입을 만들고, 원하는 전문가의 시간을 예약해 보세요.
            </p>
          </div>

          {/* CTA Buttons */}
          <div className="flex items-center justify-center gap-4 mb-16 mobile:flex-col mobile:gap-3">
            <button
              onClick={() => navigate("/login")}
              className="px-8 py-4 bg-primary-600 hover:bg-primary-700 text-white rounded-2xl text-[15px] font-bold shadow-lg shadow-primary-500/25 transition-all duration-200 hover:-translate-y-0.5 active:translate-y-0 flex items-center justify-center gap-2 mobile:w-full"
            >
              <span>🚀 1분 만에 내 시간 판매 시작하기</span>
            </button>
            <button
              onClick={() => navigate("/explore")}
              className="px-8 py-4 bg-white hover:bg-achromatic-50 text-achromatic-800 border border-achromatic-200 rounded-2xl text-[15px] font-bold transition-all duration-200 hover:-translate-y-0.5 active:translate-y-0 shadow-sm flex items-center justify-center gap-2 mobile:w-full"
            >
              <span>👀 등록된 서비스 둘러보기</span>
            </button>
          </div>

          {/* ━━ Hero Visual Mockup Cards (시선을 사로잡는 플로팅 세션 카드들) ━━ */}
          <div className="relative max-w-[960px] mx-auto">
            <div className="grid grid-cols-3 gap-5 mobile:grid-cols-1">
              
              {/* Card 1: 🔮 타로 리딩 */}
              <div className="bg-white/95 backdrop-blur-md rounded-2xl p-5 border border-violet-100 shadow-xl shadow-violet-500/5 hover:-translate-y-1 transition-all duration-300">
                <div className="flex items-center justify-between mb-3">
                  <span className="px-2.5 py-1 rounded-lg bg-violet-50 text-violet-700 text-[11px] font-bold">🔮 타로·운세</span>
                  <span className="text-[11px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">● 오늘 20:00 가능</span>
                </div>
                <h3 className="text-[15px] font-bold text-achromatic-900 mb-2 leading-snug">
                  연애 & 재회운 1:1 심층 타로 리딩
                </h3>
                <p className="text-[12px] text-achromatic-400 mb-4 line-clamp-2">
                  "상대방이 진짜 무슨 생각을 하는지 속마음을 솔직하게 들려드려요."
                </p>
                <div className="flex items-center justify-between pt-3 border-t border-achromatic-100">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-violet-100 flex items-center justify-center text-xs font-bold text-violet-700">유나</div>
                    <span className="text-xs text-achromatic-600 font-medium">타로마스터 ★ 4.9</span>
                  </div>
                  <span className="text-sm font-extrabold text-achromatic-900">15,000원 <span className="text-xs font-normal text-achromatic-400">/ 30분</span></span>
                </div>
              </div>

              {/* Card 2: 💬 30분 티타임 (메인 강조) */}
              <div className="bg-gradient-to-b from-white to-primary-50/40 rounded-2xl p-5 border-2 border-primary-500 shadow-2xl shadow-primary-500/15 -translate-y-3 mobile:translate-y-0 relative">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 bg-primary-600 text-white rounded-full text-[10px] font-extrabold uppercase tracking-wider shadow-sm">
                  MOST POPULAR
                </div>
                <div className="flex items-center justify-between mb-3 mt-1">
                  <span className="px-2.5 py-1 rounded-lg bg-pink-50 text-pink-700 text-[11px] font-bold">💬 수다·티타임</span>
                  <span className="text-[11px] font-bold text-primary-600 bg-primary-50 px-2 py-0.5 rounded-full">실시간 입장 링크</span>
                </div>
                <h3 className="text-[16px] font-extrabold text-achromatic-900 mb-2 leading-snug">
                  오늘 하루 힘들었죠? 들어주는 30분
                </h3>
                <p className="text-[12px] text-achromatic-500 mb-4 line-clamp-2">
                  친구에게 말 못 할 고민, 따뜻하게 경청하고 비밀은 100% 보장됩니다.
                </p>
                <div className="flex items-center justify-between pt-3 border-t border-primary-100">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-pink-100 flex items-center justify-center text-xs font-bold text-pink-700">라떼</div>
                    <span className="text-xs text-achromatic-700 font-medium">따뜻한호스트 ★ 5.0</span>
                  </div>
                  <span className="text-sm font-extrabold text-primary-600">10,000원 <span className="text-xs font-normal text-achromatic-400">/ 30분</span></span>
                </div>
              </div>

              {/* Card 3: 🎮 게임 듀오 */}
              <div className="bg-white/95 backdrop-blur-md rounded-2xl p-5 border border-indigo-100 shadow-xl shadow-indigo-500/5 hover:-translate-y-1 transition-all duration-300">
                <div className="flex items-center justify-between mb-3">
                  <span className="px-2.5 py-1 rounded-lg bg-indigo-50 text-indigo-700 text-[11px] font-bold">🎮 게임·듀오</span>
                  <span className="text-[11px] font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">주말 예약 가능</span>
                </div>
                <h3 className="text-[15px] font-bold text-achromatic-900 mb-2 leading-snug">
                  롤(LoL) 다이아 서폿 멘탈 케어 듀오
                </h3>
                <p className="text-[12px] text-achromatic-400 mb-4 line-clamp-2">
                  티어 상승 라인전 운영 코칭! 즐겜도 빡겜도 모두 환영합니다.
                </p>
                <div className="flex items-center justify-between pt-3 border-t border-achromatic-100">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center text-xs font-bold text-indigo-700">협곡</div>
                    <span className="text-xs text-achromatic-600 font-medium">다이아1 ★ 4.9</span>
                  </div>
                  <span className="text-sm font-extrabold text-achromatic-900">20,000원 <span className="text-xs font-normal text-achromatic-400">/ 60분</span></span>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>


      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
          2. CATEGORY QUICK EXPLORER (카테고리 둘러보기)
         ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="py-16 mobile:py-12 bg-white border-b border-achromatic-100">
        <div className="max-w-[1140px] mx-auto px-6 mobile:px-5">
          <div className="text-center mb-10">
            <h2 className="text-[26px] mobile:text-[20px] font-extrabold text-achromatic-900 mb-2">
              어떤 시간과 경험을 나누고 싶으신가요?
            </h2>
            <p className="text-sm text-achromatic-400">
              취향과 목적에 맞는 세션을 클릭해 바로 확인해보세요
            </p>
          </div>

          <div className="grid grid-cols-3 mobile:grid-cols-2 gap-4">
            {categories.map((c) => (
              <div
                key={c.label}
                onClick={() => navigate(`/explore?category=${c.category}`)}
                className={`p-5 rounded-2xl border bg-gradient-to-br ${c.color} hover:shadow-md transition-all duration-200 cursor-pointer group flex flex-col justify-between`}
              >
                <div>
                  <div className="text-3xl mb-3 group-hover:scale-110 transition-transform duration-200">
                    {c.emoji}
                  </div>
                  <h3 className="text-[16px] font-bold text-achromatic-900 mb-1 group-hover:text-primary-600 transition-colors">
                    {c.label}
                  </h3>
                  <p className="text-[12px] text-achromatic-500 leading-relaxed">
                    {c.sub}
                  </p>
                </div>
                <div className="mt-4 pt-3 border-t border-achromatic-200/40 flex items-center justify-between text-xs font-semibold text-achromatic-600">
                  <span>둘러보기</span>
                  <span className="group-hover:translate-x-1 transition-transform">→</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
          3. CURATED INSPIRATION ("이런 것도 거래돼요!")
         ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="py-20 mobile:py-14 bg-achromatic-50/60 border-b border-achromatic-100">
        <div className="max-w-[1140px] mx-auto px-6 mobile:px-5">
          <div className="flex justify-between items-end mb-10 mobile:flex-col mobile:items-start mobile:gap-2">
            <div>
              <span className="text-xs font-bold text-primary-600 uppercase tracking-wider bg-primary-50 px-2.5 py-1 rounded-md mb-2 inline-block">
                INSPIRING SESSIONS
              </span>
              <h2 className="text-[28px] mobile:text-[22px] font-extrabold text-achromatic-900">
                실제 이런 특별한 시간들이 오가고 있어요
              </h2>
              <p className="text-sm text-achromatic-400 mt-1">
                거창한 자격증이 없어도 괜찮아요. 나만의 경험과 따뜻한 관심이 누군가에게는 소중한 해결책입니다.
              </p>
            </div>
            <button
              onClick={() => navigate("/explore")}
              className="text-sm font-bold text-primary-600 hover:text-primary-700 transition-colors flex items-center gap-1"
            >
              전체 세션 보러가기 →
            </button>
          </div>

          <div className="grid grid-cols-3 mobile:grid-cols-1 gap-6">
            {/* DB에 등록된 실 서비스가 있으면 먼저 띄우고, 부족하면 매력적인 큐레이션 샘플을 결합 */}
            {(dbServices.length > 0 ? dbServices : mockCuratedServices).slice(0, 6).map((service: any) => {
              const mainImg = service.images && service.images.length > 0 ? service.images[0] : null;
              const hasImg = Boolean(mainImg);

              return (
                <div
                  key={service.id}
                  onClick={() => navigate(service.id.startsWith("sample-") ? "/explore" : `/service/${service.id}`)}
                  className="bg-white rounded-2xl overflow-hidden border border-achromatic-100 hover:border-primary-300 hover:shadow-xl hover:shadow-primary-500/10 transition-all duration-300 cursor-pointer flex flex-col group"
                >
                  {/* Visual Header */}
                  <div className={`w-full h-40 relative flex items-center justify-center overflow-hidden ${hasImg ? 'bg-achromatic-100' : 'bg-gradient-to-br ' + (service.bgGradient || 'from-primary-600 to-indigo-700')}`}>
                    {hasImg ? (
                      <img
                        src={getImageUrl(mainImg)}
                        alt={service.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <span className="text-5xl group-hover:scale-125 transition-transform duration-300 select-none">
                        {service.emoji || "✨"}
                      </span>
                    )}
                    <div className="absolute top-3 left-3">
                      <span className="px-2.5 py-1 rounded-lg bg-black/50 backdrop-blur-md text-white text-[11px] font-bold">
                        {service.category}
                      </span>
                    </div>
                    <div className="absolute top-3 right-3">
                      <span className="px-2.5 py-1 rounded-lg bg-emerald-500/90 backdrop-blur-md text-white text-[10px] font-bold">
                        {service.slotsText || "예약 가능"}
                      </span>
                    </div>
                  </div>

                  {/* Body Content */}
                  <div className="p-5 flex-1 flex flex-col justify-between">
                    <div>
                      <h3 className="text-[16px] font-bold text-achromatic-900 group-hover:text-primary-600 transition-colors line-clamp-1 mb-2">
                        {service.title}
                      </h3>
                      <p className="text-xs text-achromatic-400 line-clamp-2 leading-relaxed mb-4">
                        {service.description}
                      </p>
                    </div>

                    <div className="pt-4 border-t border-achromatic-100 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center text-[10px] font-bold text-primary-700">
                          {service.host?.name?.charAt(0) ?? "호"}
                        </div>
                        <span className="text-xs font-medium text-achromatic-600 truncate max-w-[100px]">
                          {service.host?.name ?? "호스트"}
                        </span>
                      </div>
                      <div className="text-right">
                        <span className="text-[17px] font-extrabold text-achromatic-900">
                          {Number(service.price).toLocaleString()}원
                        </span>
                        <span className="text-[11px] text-achromatic-400 block -mt-0.5">
                          {service.durationMinutes || 30}분 기준
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>


      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
          4. WHY TIPOT? (안전 & 신뢰 가치 제안)
         ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="py-20 mobile:py-14 bg-white border-b border-achromatic-100">
        <div className="max-w-[1140px] mx-auto px-6 mobile:px-5">
          <div className="text-center max-w-[600px] mx-auto mb-14">
            <span className="text-xs font-bold text-primary-600 uppercase tracking-wider bg-primary-50 px-2.5 py-1 rounded-md mb-2 inline-block">
              SAFE & SIMPLE
            </span>
            <h2 className="text-[28px] mobile:text-[22px] font-extrabold text-achromatic-900 mb-2">
              왜 사람들은 TiPot을 신뢰할까요?
            </h2>
            <p className="text-sm text-achromatic-400">
              불안한 개인 간 거래 대신, 플랫폼이 결제부터 링크 입장, 정산까지 모두 책임집니다.
            </p>
          </div>

          <div className="grid grid-cols-3 mobile:grid-cols-1 gap-6">
            <div className="bg-gradient-to-b from-achromatic-50 to-white p-8 rounded-3xl border border-achromatic-100 hover:shadow-lg transition-all duration-300">
              <div className="w-14 h-14 rounded-2xl bg-amber-100 flex items-center justify-center text-2xl mb-6 shadow-sm">
                🛡️
              </div>
              <h3 className="text-[18px] font-extrabold text-achromatic-900 mb-2.5">
                100% 안전 에스크로 결제
              </h3>
              <p className="text-sm text-achromatic-500 leading-relaxed">
                결제된 금액은 서비스가 정상 완료될 때까지 플랫폼이 안전하게 보관합니다. 호스트 노쇼나 문제 발생 시 100% 환불을 보장합니다.
              </p>
            </div>

            <div className="bg-gradient-to-b from-achromatic-50 to-white p-8 rounded-3xl border border-achromatic-100 hover:shadow-lg transition-all duration-300">
              <div className="w-14 h-14 rounded-2xl bg-indigo-100 flex items-center justify-center text-2xl mb-6 shadow-sm">
                ⚡
              </div>
              <h3 className="text-[18px] font-extrabold text-achromatic-900 mb-2.5">
                원클릭 화상 링크 자동 연결
              </h3>
              <p className="text-sm text-achromatic-500 leading-relaxed">
                서로 번호를 교환할 필요 없이, 예약 시간 10분 전에 호스트가 지정한 Zoom, Google Meet, 디스코드 링크가 자동으로 마이페이지에 열립니다.
              </p>
            </div>

            <div className="bg-gradient-to-b from-achromatic-50 to-white p-8 rounded-3xl border border-achromatic-100 hover:shadow-lg transition-all duration-300">
              <div className="w-14 h-14 rounded-2xl bg-emerald-100 flex items-center justify-center text-2xl mb-6 shadow-sm">
                💰
              </div>
              <h3 className="text-[18px] font-extrabold text-achromatic-900 mb-2.5">
                투명한 세무 & 간편 출금
              </h3>
              <p className="text-sm text-achromatic-500 leading-relaxed">
                합리적인 플랫폼 수수료(15%)와 3.3% 사업소득세 원천징수를 자동 계산하여 호스트의 소중한 정산금을 통장으로 안전하게 지급해 드립니다.
              </p>
            </div>
          </div>
        </div>
      </section>


      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
          5. HOW IT WORKS (초간단 4단계 가이드)
         ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="py-20 mobile:py-14 bg-achromatic-50/50 border-b border-achromatic-100">
        <div className="max-w-[1140px] mx-auto px-6 mobile:px-5">
          <div className="text-center max-w-[500px] mx-auto mb-14">
            <span className="text-xs font-bold text-primary-600 uppercase tracking-wider bg-primary-50 px-2.5 py-1 rounded-md mb-2 inline-block">
              STEP BY STEP
            </span>
            <h2 className="text-[28px] mobile:text-[22px] font-extrabold text-achromatic-900 mb-2">
              이용 방법은 정말 간단해요
            </h2>
            <p className="text-sm text-achromatic-400">
              복잡한 절차 없이 카카오 3초 가입 후 바로 시작할 수 있습니다.
            </p>
          </div>

          <div className="grid grid-cols-4 mobile:grid-cols-1 gap-6 relative">
            <div className="bg-white p-6 rounded-2xl border border-achromatic-100 relative">
              <div className="w-10 h-10 rounded-xl bg-primary-600 text-white font-extrabold flex items-center justify-center mb-4 text-sm shadow-md shadow-primary-500/20">
                01
              </div>
              <span className="text-[10px] font-bold text-primary-600 bg-primary-50 px-2 py-0.5 rounded uppercase mb-2 inline-block">
                누구나 가능
              </span>
              <h3 className="text-[15px] font-bold text-achromatic-900 mb-1.5">
                시간 & 내용 등록
              </h3>
              <p className="text-xs text-achromatic-400 leading-relaxed">
                자신이 가능한 요일과 시간대, 30분당 가격, 만날 온라인 링크를 적어 올립니다.
              </p>
            </div>

            <div className="bg-white p-6 rounded-2xl border border-achromatic-100 relative">
              <div className="w-10 h-10 rounded-xl bg-primary-600 text-white font-extrabold flex items-center justify-center mb-4 text-sm shadow-md shadow-primary-500/20">
                02
              </div>
              <span className="text-[10px] font-bold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded uppercase mb-2 inline-block">
                게스트
              </span>
              <h3 className="text-[15px] font-bold text-achromatic-900 mb-1.5">
                원하는 시간 예약·결제
              </h3>
              <p className="text-xs text-achromatic-400 leading-relaxed">
                호스트의 캘린더에서 비어있는 시간대를 골라 카드로 즉시 결제합니다.
              </p>
            </div>

            <div className="bg-white p-6 rounded-2xl border border-achromatic-100 relative">
              <div className="w-10 h-10 rounded-xl bg-primary-600 text-white font-extrabold flex items-center justify-center mb-4 text-sm shadow-md shadow-primary-500/20">
                03
              </div>
              <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded uppercase mb-2 inline-block">
                온라인 진행
              </span>
              <h3 className="text-[15px] font-bold text-achromatic-900 mb-1.5">
                10분 전 자동 링크 입장
              </h3>
              <p className="text-xs text-achromatic-400 leading-relaxed">
                시작 10분 전 마이페이지의 '입장하기' 버튼이 활성화되어 바로 세션에 접속합니다.
              </p>
            </div>

            <div className="bg-white p-6 rounded-2xl border border-achromatic-100 relative">
              <div className="w-10 h-10 rounded-xl bg-primary-600 text-white font-extrabold flex items-center justify-center mb-4 text-sm shadow-md shadow-primary-500/20">
                04
              </div>
              <span className="text-[10px] font-bold text-purple-600 bg-purple-50 px-2 py-0.5 rounded uppercase mb-2 inline-block">
                수익 창출
              </span>
              <h3 className="text-[15px] font-bold text-achromatic-900 mb-1.5">
                세션 완료 & 정산
              </h3>
              <p className="text-xs text-achromatic-400 leading-relaxed">
                시간이 종료되면 호스트 지갑에 정산 대금이 지급되며 계좌로 바로 출금합니다.
              </p>
            </div>
          </div>
        </div>
      </section>


      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
          6. HIGH-CONVERTING FINAL CTA (시선을 사로잡는 그라데이션 배너)
         ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="py-20 mobile:py-16 bg-gradient-to-br from-primary-900 via-indigo-900 to-violet-950 text-white relative overflow-hidden">
        {/* Background ambient effect */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] bg-primary-500/20 rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-[700px] mx-auto px-6 text-center relative z-10">
          <span className="inline-block px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-md text-primary-200 text-xs font-bold mb-6 border border-white/10">
            당신의 시간도 누군가에겐 꼭 필요합니다
          </span>
          <h2 className="text-[38px] mobile:text-[26px] font-extrabold leading-[1.25] mb-5 tracking-tight">
            오늘 남는 시간 30분,<br />
            특별한 수익과 만남으로 바꿔볼까요?
          </h2>
          <p className="text-base mobile:text-sm text-primary-100/80 mb-10 leading-relaxed font-light">
            복잡한 승인 절차 없이, 카카오 계정으로 3초 만에 가입하고 지금 바로 시작하세요.
          </p>

          <div className="flex items-center justify-center gap-4 mobile:flex-col mobile:gap-3">
            <button
              onClick={() => navigate("/login")}
              className="px-9 py-4 bg-white hover:bg-primary-50 text-primary-900 rounded-2xl text-[15px] font-extrabold shadow-xl transition-all duration-200 hover:scale-105 active:scale-100 mobile:w-full flex items-center justify-center gap-2"
            >
              <span>⚡ 카카오로 3초 만에 시작하기</span>
            </button>
            <button
              onClick={() => navigate("/explore")}
              className="px-9 py-4 bg-white/10 hover:bg-white/20 text-white border border-white/20 rounded-2xl text-[15px] font-bold backdrop-blur-sm transition-all duration-200 mobile:w-full"
            >
              <span>서비스 둘러보기</span>
            </button>
          </div>
        </div>
      </section>


      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
          7. FOOTER (정돈된 기업 정보 및 필수 안내)
         ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <footer className="border-t border-achromatic-100 bg-achromatic-50/80">
        <div className="max-w-[1140px] mx-auto px-6 py-14 mobile:px-5">
          <div className="flex justify-between gap-16 mobile:flex-col mobile:gap-8 mb-10">
            {/* Brand column */}
            <div className="max-w-[280px]">
              <span className="text-[20px] font-extrabold tracking-tight text-achromatic-900">TiPot</span>
              <p className="text-xs text-achromatic-400 mt-2.5 leading-relaxed">
                당신의 소중한 시간과 경험을 신뢰로 연결하는 온디맨드 1:1 세션 플랫폼
              </p>
            </div>

            {/* Nav columns */}
            <div className="flex gap-16 mobile:gap-8">
              <div>
                <h4 className="text-xs font-bold text-achromatic-800 uppercase tracking-wider mb-3.5">서비스</h4>
                <ul className="space-y-2.5 text-xs text-achromatic-500">
                  <li><button onClick={() => navigate('/explore')} className="hover:text-primary-600 transition-colors">서비스 둘러보기</button></li>
                  <li><button onClick={() => navigate('/login')} className="hover:text-primary-600 transition-colors">내 시간 판매 등록</button></li>
                  <li><button onClick={() => navigate('/login')} className="hover:text-primary-600 transition-colors">로그인 / 회원가입</button></li>
                </ul>
              </div>

              <div>
                <h4 className="text-xs font-bold text-achromatic-800 uppercase tracking-wider mb-3.5">고객 지원 & 정책</h4>
                <ul className="space-y-2.5 text-xs text-achromatic-500">
                  <li><a href="tel:050266812043" className="hover:text-primary-600 transition-colors">고객센터 0502-6681-2043</a></li>
                  <li><a href="mailto:help@jellypunch.com" className="hover:text-primary-600 transition-colors">1:1 문의 (help@jellypunch.com)</a></li>
                  <li><button onClick={() => setShowTerms(true)} className="hover:text-primary-600 transition-colors">서비스 이용약관</button></li>
                  <li><button onClick={() => setShowPrivacy(true)} className="hover:text-primary-600 transition-colors font-semibold">개인정보처리방침</button></li>
                </ul>
              </div>
            </div>
          </div>

          {/* Business Info */}
          <div className="border-t border-achromatic-200/60 pt-6 text-[11px] text-achromatic-400 leading-relaxed">
            <p>주식회사 젤리펀치 | 대표: 송창환 | 사업자등록번호: 807-81-00467 | 통신판매업신고: 2022-서울강남-01300</p>
            <p>주소: 서울시 강남구 테헤란로79길 6, 4층 브이 590호 | 전화: 0502-6681-2043 | 문의: <a href="mailto:help@jellypunch.com" className="text-achromatic-600 underline">help@jellypunch.com</a></p>
            <p className="mt-2 text-achromatic-400">
              티팟(TIPOT)은 통신판매중개자이며, 통신판매의 당사자가 아닙니다. 따라서 티팟은 호스트(판매자)가 등록한 상품, 거래 정보 및 거래에 대하여 책임을 지지 않습니다.
            </p>
            <p className="text-[10px] text-achromatic-300 mt-4">
              © {new Date().getFullYear()} Jellypunch Co., Ltd. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Modals */}
      <TermsModal isOpen={showTerms} onClose={() => setShowTerms(false)} />
      <PrivacyModal isOpen={showPrivacy} onClose={() => setShowPrivacy(false)} />
    </div>
  );
}
